import React, { useRef, useEffect, useState } from 'react';
import Layout from '../../Components/Layout';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import toast from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const WorkOrderView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const poRef = useRef(null);

    const [orderData, setOrderData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch order data by ID
    const fetchOrderById = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");

            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/orders/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            console.log("Order Data => ", response.data);
            setOrderData(response.data);
            setError(null);

        } catch (error) {
            console.error("Fetch Order Error => ", error);
            setError("Failed to fetch order details");
            toast.error("Failed To Fetch Order Details");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchOrderById();
        }
    }, [id]);

    // Format currency (preserving exact format from image)
    const formatCurrency = (value) => {
        if (value === undefined || value === null) return '₹ 0.00';
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(value);
    };

    // Handle download as JSON
    const handleDownloadJSON = () => {
        if (!orderData) return;

        const exportData = {
            ...orderData,
            summary: {
                subtotal: orderData.subTotal,
                cgst: orderData.cgst,
                sgst: orderData.sgst,
                igst: orderData.igst,
                total: orderData.total,
                generatedAt: new Date().toISOString()
            }
        };

        const dataStr = JSON.stringify(exportData, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `purchase_order_${orderData.referenceOrderNumber || orderData.orderId}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success('JSON downloaded successfully!');
    };

    // Handle PDF download
    /*
    const handleDownloadPDF = async () => {
        const element = poRef.current;
        if (!element) return;

        const downloadBtn = document.querySelector('#pdfDownloadBtn');
        const originalBtnText = downloadBtn?.innerHTML;
        if (downloadBtn) {
            downloadBtn.innerHTML = 'Generating PDF...';
            downloadBtn.disabled = true;
        }

        try {
            const originalStyles = {
                width: element.style.width,
                position: element.style.position,
                transform: element.style.transform
            };

            element.style.width = '800px';
            element.style.position = 'relative';

            const canvas = await html2canvas(element, {
                scale: 3,
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false,
                windowWidth: element.scrollWidth,
                windowHeight: element.scrollHeight,
            });

            element.style.width = originalStyles.width;
            element.style.position = originalStyles.position;

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'px',
                format: 'a4'
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = pdfWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            let position = 0;
            let heightLeft = imgHeight;

            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pdfHeight;

            while (heightLeft > 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pdfHeight;
            }

            pdf.save(`Purchase_Order_${orderData?.referenceOrderNumber || orderData?.orderId}.pdf`);
            toast.success('PDF downloaded successfully!');

        } catch (error) {
            console.error('PDF generation error:', error);
            toast.error('Error generating PDF. Please try again.');
        } finally {
            if (downloadBtn) {
                downloadBtn.innerHTML = originalBtnText;
                downloadBtn.disabled = false;
            }
        }
    };
    */
    // Handle PDF download
    const handleDownloadPDF = async () => {
        const element = poRef.current;
        if (!element) return;

        const downloadBtn = document.querySelector('#pdfDownloadBtn');
        const originalBtnText = downloadBtn?.innerHTML;
        if (downloadBtn) {
            downloadBtn.innerHTML = 'Generating PDF...';
            downloadBtn.disabled = true;
        }

        try {
            // Store original styles
            const originalOverflow = document.body.style.overflow;
            const originalWidth = element.style.width;
            const originalPadding = element.style.padding;

            // Prepare element for PDF capture
            document.body.style.overflow = 'hidden';
            element.style.width = '800px';
            element.style.padding = '20px';
            element.style.boxSizing = 'border-box';

            // Wait for any reflows
            await new Promise(resolve => setTimeout(resolve, 100));

            const canvas = await html2canvas(element, {
                scale: 3,
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false,
                windowWidth: element.scrollWidth,
                windowHeight: element.scrollHeight,
                onclone: (clonedDoc, element) => {
                    // Fix styling in cloned element
                    const clonedInvoice = clonedDoc.getElementById('purchaseOrderPdf');
                    if (clonedInvoice) {
                        clonedInvoice.style.width = '800px';
                        clonedInvoice.style.margin = '0 auto';
                        clonedInvoice.style.padding = '20px';
                        clonedInvoice.style.backgroundColor = '#ffffff';
                    }
                }
            });

            // Restore original styles
            element.style.width = originalWidth;
            element.style.padding = originalPadding;
            document.body.style.overflow = originalOverflow;

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            // Calculate image height to fit PDF width
            const imgWidth = pdfWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            let heightLeft = imgHeight;
            let position = 0;

            // Add first page
            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pdfHeight;

            // Add additional pages if needed
            while (heightLeft > 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pdfHeight;
            }

            pdf.save(`Purchase_Order_${orderData?.referenceOrderNumber || orderData?.orderId}.pdf`);
            toast.success('PDF downloaded successfully!');

        } catch (error) {
            console.error('PDF generation error:', error);
            toast.error('Error generating PDF. Please try again.');
        } finally {
            if (downloadBtn) {
                downloadBtn.innerHTML = originalBtnText;
                downloadBtn.disabled = false;
            }
        }
    };

    // Loading State
    if (loading) {
        return (
            <Layout>
                <div className="backgound-main-content py-4">
                    <div className="container-fluid text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Loading Work Order Details...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    // Error State
    if (error || !orderData) {
        return (
            <Layout>
                <div className="backgound-main-content py-4">
                    <div className="container-fluid text-center py-5">
                        <h4 className="text-danger">Error Loading Work Order</h4>
                        <p>{error || "Order not found"}</p>
                        <button
                            className="btn btn-primary mt-3"
                            onClick={() => navigate(-1)}
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    // Format customer name (firstName + lastName)
    const getCustomerName = () => {
        const name = [orderData.firstName, orderData.lastName].filter(Boolean).join(' ');
        return name || orderData.organizationName || "N/A";
    };

    // Format full address
    const getFullAddress = () => {
        if (!orderData.address) return "N/A";
        const addr = orderData.address;
        const parts = [
            addr.streetLine1,
            addr.streetLine2,
            addr.landMark,
            addr.city,
            addr.state,
            addr.postalCode,
            addr.country
        ].filter(Boolean);
        return parts.join(', ');
    };

    return (
        <Layout>
            <div className="backgound-main-content py-4">
                <div className="container-fluid">

                    {/* Top Buttons - EXACT same as image */}
                    <div className="d-flex justify-content-between mb-3">
                        <button
                            className="btn btn-secondary"
                            onClick={() => navigate(-1)}
                        >
                            ← Back
                        </button>
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-outline-primary"
                                onClick={handleDownloadJSON}
                            >
                                <i className="fas fa-download me-2"></i>
                                Download JSON
                            </button>
                            <button
                                id="pdfDownloadBtn"
                                className="btn btn-primary"
                                onClick={handleDownloadPDF}
                                style={{ backgroundColor: '#1a2c3e', borderColor: '#1a2c3e' }}
                            >
                                <i className="fas fa-file-pdf me-2"></i>
                                Download PDF
                            </button>
                        </div>
                    </div>

                    {/* Main PO Card - EXACT UI from image (no changes to structure) */}
                    <div
                        ref={poRef}
                        id="purchaseOrderPdf"
                        style={{
                            width: '800px',
                            margin: '0 auto',
                            background: '#fff',
                            border: '1px solid #000',
                            fontFamily: 'Arial, sans-serif',
                            fontSize: '12px',
                            lineHeight: '1.4',
                        }}
                    >
                        {/* Header Section - EXACT as image */}
                        <div style={{ textAlign: 'center', borderBottom: '1px solid #000', padding: '15px' }}>
                            <h2 style={{ margin: '0 0 5px 0', fontSize: '18px', fontWeight: 'bold' }}>
                                RAK SOFTECH PRIVATE LIMITED
                            </h2>
                            <div style={{ fontSize: '10px' }}>
                                office no 10, 3rd floor Metro 9 business centre, Opp Shivraj nagar BRT Stop PCMC- 411033
                            </div>
                            <div style={{ fontSize: '10px' }}>
                                📞 Phone: +91 20 4123 4567 | ✉️ admin@raksotech.com | 🌐 www.raksotech.com
                            </div>
                        </div>

                        {/* Supplier & PO Info Row - EXACT structure as image */}
                        <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                            <div style={{ width: '50%', borderRight: '1px solid #000', padding: '10px' }}>
                                <strong> Name:</strong>
                                <div style={{ marginTop: '5px' }}>{getCustomerName()}</div>
                                <div><strong>Phone:</strong> {orderData.mobileNumber || "N/A"}</div>
                                <div><strong>Fax:</strong> N/A</div>
                                <div><strong>Email:</strong> {orderData.email || "N/A"}</div>
                                <div style={{ marginTop: '5px' }}><strong>Address:</strong></div>
                                <div>{getFullAddress()}</div>
                            </div>
                            <div style={{ width: '50%', padding: '10px' }}>
                                <div><strong>Reference Order Number :</strong> {orderData.referenceOrderNumber || orderData.orderId}</div>
                                <div style={{ marginTop: '8px' }}><strong> For:</strong></div>
                                <div>{orderData.orderType || "SERVICE"}</div>

                                <div style={{ marginTop: '8px' }}><strong>Creation Date :</strong></div>
                                <div>{orderData.orderDate}</div>
                            </div>
                        </div>

                        {/* Items Table - EXACT columns from image */}
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f0f0f0', borderBottom: '1px solid #000' }}>
                                    <th style={{ padding: '8px', textAlign: 'left', width: '8%', border: '1px solid #000' }}>Item #</th>
                                    <th style={{ padding: '8px', textAlign: 'left', width: '42%', border: '1px solid #000' }}>Description</th>
                                    <th style={{ padding: '8px', textAlign: 'center', width: '10%', border: '1px solid #000' }}>Qty</th>
                                    <th style={{ padding: '8px', textAlign: 'right', width: '15%', border: '1px solid #000' }}>Unit Price</th>
                                    <th style={{ padding: '8px', textAlign: 'center', width: '10%', border: '1px solid #000' }}>Discount</th>
                                    <th style={{ padding: '8px', textAlign: 'right', width: '15%', border: '1px solid #000' }}>Price</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orderData.items && orderData.items.length > 0 ? (
                                    orderData.items.map((item, idx) => (
                                        <tr key={item.itemId || idx}>
                                            <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{idx + 1}</td>
                                            <td style={{ border: '1px solid #000', padding: '8px' }}>{item.description}</td>
                                            <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{item.quantity}</td>
                                            <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'right' }}>{formatCurrency(item.unitPrice)}</td>
                                            <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{item.discount}</td>
                                            <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(item.price)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                                            No items found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {/* Totals Section - EXACT layout as image */}
                        {/* Totals Section - EXACT layout as image */}
                        <div style={{ display: 'flex', borderBottom: '1px solid #000' }}>
                            <div style={{ width: '50%', borderRight: '1px solid #000', padding: '10px', minHeight: '80px' }}>
                                {/* Empty left side as per image */}
                            </div>
                            <div style={{ width: '50%', padding: '10px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                    <strong>Sub Total:</strong>
                                    <span>{formatCurrency(orderData.subTotal)}</span>
                                </div>

                                {orderData.gstType === "INTRA_STATE" ? (
                                    <>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                            <strong>CGST (9%):</strong>
                                            <span>{formatCurrency(orderData.cgst)}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                            <strong>SGST (9%):</strong>
                                            <span>{formatCurrency(orderData.sgst)}</span>
                                        </div>
                                    </>
                                ) : (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                        <strong>IGST (18%):</strong>
                                        <span>{formatCurrency(orderData.igst)}</span>
                                    </div>
                                )}

                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                    <strong>Discount:</strong>
                                    <span>{formatCurrency(orderData.totalDiscount || 0)}</span>
                                </div>

                                <hr style={{ margin: '8px 0' }} />

                                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '14px' }}>
                                    <strong>TOTAL:</strong>
                                    <strong>{formatCurrency(orderData.total)}</strong>
                                </div>
                            </div>
                        </div>

                        {/* Terms and Conditions Section - EXACT phrasing from image */}
                        <div style={{ borderBottom: '1px solid #000', padding: '12px' }}>
                            <strong>Terms and Conditions</strong>
                            <ul style={{ margin: '8px 0 0 20px', fontSize: '10px', paddingLeft: '0' }}>
                                <li>Supplier has to confirm PO to be duly signed by authorized signatory. PO without signed will not be consider for payable</li>
                                <li>Payment terms will be as per agreement with the range of 45 days/60 days/90 days after receipt of invoice.</li>
                                <li>All payment will be done via NEFT/RTGS. No cash payment entertain by RAK</li>
                                <li>In case Invoice due date came in between the payroll date, same will be credited to supplier account in upcoming payroll</li>
                                <li>In case of cancellation of Services or PO, same will be communicated by RAK via email.</li>
                            </ul>
                        </div>



                        {/* Footer Note - EXACT as image */}
                        <div style={{ textAlign: 'center', padding: '10px', fontSize: '10px', color: '#666' }}>
                            <i className="fas fa-check-circle me-1"></i> This is a system generated purchase order. Supplier must sign and confirm via email.
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default WorkOrderView;